import {CachedTransform, IsNumber} from "js-vextensions";
import {CreateAccessor, GetDocs, GetDoc} from "mobx-graphlink";
import {TimelineEvent} from "../../Server/DB/timelineEvents/@TimelineEvent.js";
import {GetIndexesOfDriftingEndEvents, GetSessionPeriods, GetSessionPeriods_Me} from "./@Shared/SessionPeriodUtils.js";
import {minuteInMS} from "web-vcore";
import {GetActivities} from "./activities.js";
import {MeID} from "./users.js";
import {eventTypes_userInteractions, SessionEvent} from "../../Engine/LiveSession/SessionEvent.js";

/** Returns the timeline-events for the given timeline. */
export const GetTimelineEvents = CreateAccessor((timelineID: string|n): TimelineEvent[]=>{
	return GetDocs({
		params: {filter: {timeline: {equalTo: timelineID}}},
	}, a=>a.timelineEvents);
});
/** Returns the timeline-events for the given timeline, which intersect with the time period specified. */
export const GetTimelineEventsIntersectingRange = CreateAccessor((timelineID: string, rangeStart: number, rangeEnd?: number): TimelineEvent[]=>{
	const closedEventsInRange = GetDocs({
		params: {filter: {
			//timeline: {equalTo: timelineID}, // commented, since would require composite-index
			endTime: {greaterOrEqualTo: rangeStart}, // make sure event ended after range's start
			//startTime: rangeEnd != null && {lessThan: rangeEnd}, // make sure event started prior to range's end // commented, since can only use range-filter on one field
		}}
	}, a=>a.timelineEvents).filter(event=>{
		return rangeEnd == null || event.startTime < rangeEnd; // make sure event started prior to range's end
	});

	const ongoingEvents = GetDocs({
		params: {filter: {endTime: {equalTo: null}}},
	}, a=>a.timelineEvents);
	const ongoingEventsInRange = ongoingEvents.filter(event=>(rangeEnd == null || event.startTime < rangeEnd));

	const allEventsInRange = closedEventsInRange.concat(ongoingEventsInRange);
	return allEventsInRange.filter(a=>a.timeline == timelineID).OrderBy(a=>a.startTime);
});

export const GetTimelineEvent = CreateAccessor((id: string)=>{
	return GetDoc({}, a=>a.timelineEvents.get(id));
});

/*export const GetInProgressTimelineEvents = CreateAccessor((timelineID: string)=>{
	return GetDocs({
		queryOps: [
			new WhereOp("timeline", "==", timelineID),
			new WhereOp("endTime", "<", ""), // `if value < ""` means "if value is null"
		],
	}, a=>a.timelineEvents).OrderBy(a=>a.startTime);
});*/

/*export const GetTimelineEventsIntersectingRange_SleepScheduleMode = StoreAccessor(s=>(timelineID: string, rangeStart: number, rangeEnd?: number): TimelineEvent[]=>{
	const sleepActivity = GetActivities(MeID()).find(a=>a.name == "FBA session") ?? GetActivities(MeID())[0];
	if (sleepActivity == null) return [];
	
	const sessionPeriods = GetSessionPeriods_Me();
	const periodsInRange = sessionPeriods.filter(period=>{
		const periodIsFullyBeforeRange = period.endTime && period.endTime < rangeStart;
		const periodIsFullyAfterRange = rangeEnd && period.startTime > rangeEnd;
		const periodOverlapsRange = !periodIsFullyBeforeRange && !periodIsFullyAfterRange;
		return periodOverlapsRange;
	});

	return periodsInRange.map(period=>{
		const allEvents = period.eventGroups.SelectMany(a=>a.events);
		const firstResleepEndEvent = allEvents.find(a=>a.type == "Journey.ResleepEnd");
		if (firstResleepEndEvent == null) return null;
		const lastDreamSegment = period.journalSegments.filter(a=>a.wakeTime == null).LastOrX();
		if (lastDreamSegment == null) return null;
		const segmentsAfterLastDream = period.journalSegments.slice(period.journalSegments.indexOf(lastDreamSegment) + 1);
		const firstWakeAfterLastDream = segmentsAfterLastDream.filter(a=>a.wakeTime != null).FirstOrX()?.wakeTime ?? period.endTime;
		if (firstWakeAfterLastDream == null) return null;
		
		// consider sleep to have started 80-minutes before first resleep-end
		const actualSleepStartTime = firstResleepEndEvent.date - (80 * minuteInMS);
		// consider sleep to have ended at the time of the first "wake" journal-segment after the last "dream" journal-segment
		const actualSleepEndTime = firstWakeAfterLastDream;
		const event = new TimelineEvent({
			timeline: timelineID,
			startTime: actualSleepStartTime,
			endTime: actualSleepEndTime,
			activity: sleepActivity.id,
		});
		return event;
	}).filter(a=>a) as TimelineEvent[];
});*/

export const GetTimelineEventsIntersectingRange_SleepScheduleMode = CreateAccessor((timelineID: string, rangeStart: number, rangeEnd?: number): TimelineEvent[]=>{
	const sleepActivity = GetActivities(MeID()).find(a=>a.name == "FBA session") ?? GetActivities(MeID())[0];
	if (sleepActivity == null) return [];

	const sessionPeriods = GetSessionPeriods_Me();
	const periodsInRange = sessionPeriods.filter(period=>{
		if (period.endTime == null) return false; // there are too many periods with no end-time, so just ignore all without end-time
		const periodIsFullyBeforeRange = /*period.endTime &&*/ period.endTime < rangeStart;
		const periodIsFullyAfterRange = rangeEnd && period.startTime > rangeEnd;
		const periodOverlapsRange = !periodIsFullyBeforeRange && !periodIsFullyAfterRange;
		return periodOverlapsRange;
	});

	const allEventsInRange = periodsInRange.SelectMany(period=>period.eventGroups.SelectMany(a=>{
		if (a.sessionInfo?.launchType == "day") return [];
		return a.events;
	}));
	const allEventsInRange_driftingEndIndexes = GetIndexesOfDriftingEndEvents(allEventsInRange);

	const sleepPeriodTimelineEvents = [] as TimelineEvent[];
	let lastDriftingStartEvent: SessionEvent|n;
	let lastAlarmSequenceStart: SessionEvent|n;
	let currentDriftingEndEvent: SessionEvent|n;
	for (const [i, event] of allEventsInRange.entries()) {
		const prevEvent = allEventsInRange[i - 1] as SessionEvent|n;
		let addSleepPeriodNow = false;

		const inDriftingPeriod = lastDriftingStartEvent && (!lastAlarmSequenceStart || lastAlarmSequenceStart.date < lastDriftingStartEvent.date);
		if (
			// if a: this is a user-interaction event
			eventTypes_userInteractions.includes(event.type)
			// and b: there's gap of 30m+ since last event
			&& prevEvent && event.date - prevEvent.date >= (30 * minuteInMS)
			// and c: we're not in a drifting-period (since 30m+ gaps within drifting-periods can easily be awake-periods)
			&& !inDriftingPeriod
			// ...then assume user was asleep during it (and just slept through alarms)
		) {
			//currentResleepEndOrEquivalentEvent ??= event;
			const timelineEvent = new TimelineEvent({
				timeline: timelineID,
				startTime: prevEvent.date,
				endTime: event.date,
				activity: sleepActivity.id,
			});
			sleepPeriodTimelineEvents.push(timelineEvent);
		}

		if (event.type == "General.SessionStart") {
			currentDriftingEndEvent = null;
		} else if (event.type == "Alarms.PhaseSet" && event.phase == "drifting") {
			lastDriftingStartEvent = event;
		} else if (allEventsInRange_driftingEndIndexes.includes(i)) {
			currentDriftingEndEvent ??= event;
		} else if (event.type == "Alarms.SequenceStart") {
			lastAlarmSequenceStart = event;
		} else if (event.type == "Alarms.SolveSuccess") {
			if (currentDriftingEndEvent) addSleepPeriodNow = true;
		} else if (event.type == "General.SessionEnd") {
			if (currentDriftingEndEvent) addSleepPeriodNow = true;
			/*else {
				const priorEvents = allEventsInRange.slice(0, i);
				let lastSessionStartEventI = priorEvents.findLastIndex(a=>a.type == "General.SessionStart");
				let lastResleepEndEventI = priorEvents.findLastIndex(a=>a.type == "Journey.ResleepEnd");
				// if, a: there is no resleep-end between here and the last session-start
				// and, b: 
				// ...then we need to add a fallback sleep-period for that whole time-range
				if (lastSessionStartEventI > lastResleepEndEventI) {
					const timelineEvent = new TimelineEvent({
						timeline: timelineID,
						startTime: priorEvents[lastSessionStartEventI].date,
						endTime: event.date,
						activity: sleepActivity.id,
					});
					sleepPeriodTimelineEvents.push(timelineEvent);
				}
			}*/
		}

		if (addSleepPeriodNow) {
			const timelineEvent = new TimelineEvent({
				timeline: timelineID,
				startTime: currentDriftingEndEvent!.date - (80 * minuteInMS),
				endTime: event.date,
				activity: sleepActivity.id,
			});
			sleepPeriodTimelineEvents.push(timelineEvent);
			currentDriftingEndEvent = null;
		}
	}
	return sleepPeriodTimelineEvents;
});

export const GetTimelineEventsIntersectingRange_VisualizationMode = CreateAccessor((timelineID: string, rangeStart: number, rangeEnd?: number): TimelineEvent[]=>{
	const visualizationActivity = GetActivities(MeID()).find(a=>a.name == "Visualization") ?? GetActivities(MeID())[0];
	if (visualizationActivity == null) return [];

	const sessionPeriods = GetSessionPeriods_Me();
	const periodsInRange = sessionPeriods.filter(period=>{
		if (period.endTime == null) return false; // ignore periods without end-time
		const periodIsFullyBeforeRange = period.endTime < rangeStart;
		const periodIsFullyAfterRange = rangeEnd && period.startTime > rangeEnd;
		const periodOverlapsRange = !periodIsFullyBeforeRange && !periodIsFullyAfterRange;
		return periodOverlapsRange;
	});

	const visualizationTimelineEvents = [] as TimelineEvent[];

	for (const period of periodsInRange) {
		for (const [index, eventGroup] of period.eventGroups.entries()) {
			const allEventsInGroup = eventGroup.events;

			for (const [event_index, event] of allEventsInGroup.entries()) {
				// When we find a Visualization.StartPrompting event, look for its corresponding ender event
				if (event.type == "Visualization.StartPrompting") {
					const futureEventsInPeriod = allEventsInGroup.slice(event_index + 1).concat(
						period.eventGroups.slice(index + 1).SelectMany(a=>a.events)
					);
					const enderEvent = futureEventsInPeriod.find(a=>a.type == "Visualization.StartPrompting" ||
						a.type == "Visualization.StopPrompting" ||
						a.type == "General.SessionEnd"
					);

					if (enderEvent) {
						const timelineEvent = new TimelineEvent({
							timeline: timelineID,
							startTime: event.date,
							endTime: enderEvent.date,
							activity: visualizationActivity.id,
						});
						visualizationTimelineEvents.push(timelineEvent);
					}
				}
			}
		}
	}

	// Post-processing: merge timeline-events that share the same separator event (done as post-processing, so this merging can be disabled using a checkbox later)
	const mergedEvents = [] as TimelineEvent[];
	let i = 0;
	while (i < visualizationTimelineEvents.length) {
		const currentEvent = visualizationTimelineEvents[i];
		let endTimeOfMergedBlockSoFar = currentEvent.endTime;

		let iOfFutureEvent = i + 1;
		// Keep merging with next events as long as they're contiguous
		while (iOfFutureEvent < visualizationTimelineEvents.length && endTimeOfMergedBlockSoFar == visualizationTimelineEvents[iOfFutureEvent].startTime) {
			endTimeOfMergedBlockSoFar = visualizationTimelineEvents[iOfFutureEvent].endTime;
			iOfFutureEvent++;
		}

		// Create merged event (or use original if no merging occurred)
		const mergedEvent = new TimelineEvent({
			timeline: timelineID,
			startTime: currentEvent.startTime,
			endTime: endTimeOfMergedBlockSoFar,
			activity: visualizationActivity.id,
		});
		mergedEvents.push(mergedEvent);

		// Move to the next unprocessed event
		i = iOfFutureEvent;
	}

	return mergedEvents;
});